import { useState, useRef, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Sidebar from '../home/components/Sidebar';
import { getChatModelConfig, chatCompletionStream, type ChatMessage } from '../../services/chat';
import { createChatMessage, createChatSession, getChatSessionMessages, getChatSessions, type ChatSession } from '../../services/chatHistory';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const WELCOME_MESSAGE: Message = {
  id: 'welcome',
  type: 'assistant',
  content: '你好！我是AI对话助手，很高兴为你服务。我可以帮你解答问题、提供建议、进行创意讨论等。请问有什么我可以帮助你的吗？',
  timestamp: new Date(),
};

export default function AIChatPage() {
  const [activeMenu, setActiveMenu] = useState('AI对话助手');
  const [messages, setMessages] = useState<Message[]>([WELCOME_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showQuickPrompts, setShowQuickPrompts] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chatConfig, setChatConfig] = useState<Awaited<ReturnType<typeof getChatModelConfig>>>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [sessionList, setSessionList] = useState<ChatSession[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    getChatModelConfig().then(setChatConfig);
  }, []);

  const quickPrompts = [
    {
      id: 1,
      title: '生成视频提示词',
      description: '帮我生成一个专业的文生视频提示词',
      prompt: '请帮我生成一个专业的文生视频提示词，要求画面精美、镜头运动流畅、细节丰富。主题是：[请描述你想要的视频主题]',
      icon: 'ri-video-line',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 2,
      title: '优化视频脚本',
      description: '优化视频脚本结构和画面描述',
      prompt: '请帮我优化以下视频脚本，使其更加专业、画面描述更生动、镜头运动更流畅：\n[请粘贴你的视频脚本]',
      icon: 'ri-file-edit-line',
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 3,
      title: '图生视频提示词',
      description: '根据图片生成视频提示词',
      prompt: '我有一张图片，请帮我生成适合的图生视频提示词，要求：\n1. 保持图片原有风格和主题\n2. 添加合适的镜头运动\n3. 增强画面细节描述\n\n图片内容描述：[请描述你的图片内容]',
      icon: 'ri-image-add-line',
      color: 'from-green-500 to-teal-500'
    },
    {
      id: 4,
      title: '创意灵感建议',
      description: '获取视频创作灵感和建议',
      prompt: '我想创作一个视频，但还没有明确的想法。请根据以下信息给我一些创意建议：\n\n目标受众：[描述目标观众]\n视频用途：[如：营销、教育、娱乐等]\n风格偏好：[如：科技感、温馨、酷炫等]',
      icon: 'ri-lightbulb-line',
      color: 'from-orange-500 to-red-500'
    },
    {
      id: 5,
      title: '文生图提示词优化',
      description: '优化AI绘图提示词',
      prompt: '请帮我优化以下AI绘图提示词，使其更加专业和精确：\n[请输入你的原始提示词]\n\n要求：\n1. 增强画面细节描述\n2. 添加艺术风格说明\n3. 优化光影和构图描述',
      icon: 'ri-palette-line',
      color: 'from-indigo-500 to-purple-500'
    },
    {
      id: 6,
      title: '视频分镜脚本',
      description: '生成专业的视频分镜脚本',
      prompt: '请帮我生成一个专业的视频分镜脚本，包含以下信息：\n\n视频主题：[描述主题]\n视频时长：[如：15秒、30秒等]\n目标效果：[描述想要达到的效果]\n\n要求：\n1. 包含详细的镜头描述\n2. 标注每个镜头的时长\n3. 说明镜头运动方式',
      icon: 'ri-movie-2-line',
      color: 'from-pink-500 to-rose-500'
    }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // 当有用户消息时隐藏快捷提示词
    const hasUserMessage = messages.some(msg => msg.type === 'user');
    setShowQuickPrompts(!hasUserMessage);
  }, [messages]);

  const handleQuickPromptClick = (prompt: string) => {
    setInputValue(prompt);
    textareaRef.current?.focus();
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;
    if (!chatConfig?.model_name) {
      setError('请先在后台配置并启用文本/对话模型');
      return;
    }
    if (chatConfig.provider !== 'alibaba' && !chatConfig.api_key) {
      setError('当前文本模型缺少可用密钥。请启用阿里文本模型，或重新配置火山引擎/钱多多密钥');
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setError(null);
    setIsTyping(true);

    let sessionId = currentSessionId;
    if (!sessionId) {
      const createResult = await createChatSession(userMessage.content.slice(0, 30));
      if (createResult.error || !createResult.id) {
        setIsTyping(false);
        setError(createResult.error || '创建会话失败');
        return;
      }
      sessionId = createResult.id;
      setCurrentSessionId(sessionId);
    }

    await createChatMessage(sessionId, { role: 'user', content: userMessage.content });

    const streamingMsgId = `streaming-${Date.now()}`;
    const placeholderMessage: Message = {
      id: streamingMsgId,
      type: 'assistant',
      content: '',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, placeholderMessage]);
    let assistantReply = '';

    try {
      const history: ChatMessage[] = messages
        .filter((m) => !(m.type === 'assistant' && m.id === 'welcome'))
        .map((m) => ({ role: m.type as 'user' | 'assistant', content: m.content }));
      const chatMessages: ChatMessage[] = [...history, { role: 'user', content: userMessage.content }];

      await chatCompletionStream(chatConfig, chatMessages, (chunk) => {
        assistantReply += chunk;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === streamingMsgId ? { ...m, content: m.content + chunk } : m
          )
        );
      });
      if (sessionId) {
        const assistantMessage = assistantReply.trim() || '抱歉，当前无法回复。';
        await createChatMessage(sessionId, { role: 'assistant', content: assistantMessage });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : '对话请求失败，请重试');
      setMessages((prev) =>
        prev.map((m) =>
          m.id === streamingMsgId
            ? {
                ...m,
                content: '抱歉，当前无法回复。请检查网络与后台模型配置后重试。',
              }
            : m
        )
      );
    } finally {
      setIsTyping(false);
    }
  };

  const loadSessions = async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    const result = await getChatSessions(50);
    if (result.error) {
      setHistoryError(result.error);
      setSessionList([]);
      setHistoryLoading(false);
      return;
    }
    setSessionList(result.sessions);
    setHistoryLoading(false);
  };

  const handleOpenHistory = async () => {
    setShowHistoryModal(true);
    await loadSessions();
  };

  const handleSelectSession = async (sessionId: string) => {
    const result = await getChatSessionMessages(sessionId);
    if (result.error) {
      setHistoryError(result.error);
      return;
    }
    const mapped = result.messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({
        id: m.id,
        type: m.role as 'user' | 'assistant',
        content: m.content,
        timestamp: new Date(m.created_at),
      }));
    setMessages(mapped.length > 0 ? mapped : [{ ...WELCOME_MESSAGE, id: 'welcome', timestamp: new Date() }]);
    setCurrentSessionId(sessionId);
    setShowHistoryModal(false);
    setError(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = () => {
    setMessages([{ ...WELCOME_MESSAGE, id: 'welcome', timestamp: new Date() }]);
    setCurrentSessionId(null);
    setError(null);
  };

  const formatTime = (date: Date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  return (
    <div className="h-screen overflow-hidden bg-[#0a0a0a] text-white flex">
      <Sidebar activeMenu={activeMenu} onMenuChange={setActiveMenu} />
      
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <Navbar />
        
        <main className="flex-1 flex flex-col overflow-hidden min-w-0 min-h-0">
          {/* Chat Header */}
          <div className="px-8 py-6 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold mb-1">AI 对话助手</h1>
                <p className="text-sm text-gray-400">智能对话，随时为你解答问题</p>
                {error && (
                  <div className="mt-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    {error}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleOpenHistory}
                  className="px-4 py-2 rounded-lg bg-[#1a1a1a] text-gray-300 hover:bg-[#252525] transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-history-line mr-2"></i>
                  历史记录
                </button>
                <button
                  type="button"
                  onClick={handleClearChat}
                  className="px-4 py-2 rounded-lg bg-[#1a1a1a] text-gray-300 hover:bg-[#252525] transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-delete-bin-line mr-2"></i>
                  清空对话
                </button>
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 min-h-0 overflow-y-auto px-8 py-6">
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Quick Prompts */}
              {showQuickPrompts && (
                <div className="mb-8">
                  <h2 className="text-xl font-bold text-center mb-6">有什么我能帮你的吗？</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {quickPrompts.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleQuickPromptClick(item.prompt)}
                        className="bg-[#1a1a1a] border border-gray-800 rounded-xl p-4 text-left hover:border-blue-500/50 transition-all group cursor-pointer"
                      >
                        <div className={`w-10 h-10 bg-gradient-to-br ${item.color} rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                          <i className={`${item.icon} text-white text-lg`}></i>
                        </div>
                        <h3 className="text-sm font-semibold mb-1 text-white group-hover:text-blue-400 transition-colors">
                          {item.title}
                        </h3>
                        <p className="text-xs text-gray-400 line-clamp-2">
                          {item.description}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-4 ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  {/* Avatar */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.type === 'user' 
                      ? 'bg-gradient-to-br from-blue-500 to-purple-600' 
                      : 'bg-gradient-to-br from-teal-500 to-emerald-600'
                  }`}>
                    <i className={`${message.type === 'user' ? 'ri-user-3-fill' : 'ri-robot-2-fill'} text-white text-lg`}></i>
                  </div>

                  {/* Message Content */}
                  <div className={`flex-1 ${message.type === 'user' ? 'flex flex-col items-end' : ''}`}>
                    <div className={`inline-block max-w-[80%] rounded-2xl px-5 py-3 ${
                      message.type === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-[#1a1a1a] text-gray-100'
                    }`}>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {message.content}
                        {/* 流式输出中、内容尚未到达时显示光标 */}
                        {isTyping && message.type === 'assistant' && !message.content && (
                          <span className="inline-block w-2 h-4 ml-0.5 bg-teal-400/80 animate-pulse align-middle" />
                        )}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500 mt-2 px-2">{formatTime(message.timestamp)}</span>
                  </div>
                </div>
              ))}

              {/* Typing Indicator：仅在尚未插入助手占位气泡时显示 */}
              {isTyping && messages.length > 0 && messages[messages.length - 1].type !== 'assistant' && (
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                    <i className="ri-robot-2-fill text-white text-lg"></i>
                  </div>
                  <div className="bg-[#1a1a1a] rounded-2xl px-5 py-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-800 px-8 py-6 bg-[#0a0a0a]">
            <div className="max-w-4xl mx-auto">
              <div className="bg-[#1a1a1a] rounded-2xl border border-gray-800 focus-within:border-blue-600 transition-colors">
                <textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="输入你的问题或想法..."
                  className="w-full bg-transparent px-5 py-4 text-sm text-white placeholder-gray-500 resize-none outline-none"
                  rows={3}
                  style={{ maxHeight: '200px' }}
                />
                <div className="px-5 py-3 border-t border-gray-800 flex items-center justify-end">
                  <button
                    onClick={handleSend}
                    disabled={!inputValue.trim()}
                    className={`px-6 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      inputValue.trim()
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <i className="ri-send-plane-fill mr-2"></i>
                    发送
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 text-center mt-3">
                按 Enter 发送消息，Shift + Enter 换行
              </p>
            </div>
          </div>
        </main>
      </div>

      {showHistoryModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowHistoryModal(false)}>
          <div className="w-full max-w-2xl max-h-[80vh] bg-[#111] border border-gray-800 rounded-xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-base font-semibold">历史对话记录</h3>
              <button type="button" onClick={() => setShowHistoryModal(false)} className="w-8 h-8 rounded-full hover:bg-[#1f1f1f]">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {historyLoading ? (
                <p className="text-sm text-gray-400">加载中...</p>
              ) : historyError ? (
                <div className="space-y-3">
                  <p className="text-sm text-red-400">{historyError}</p>
                  <button type="button" onClick={loadSessions} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm">
                    重试
                  </button>
                </div>
              ) : sessionList.length === 0 ? (
                <p className="text-sm text-gray-400">暂无历史对话</p>
              ) : (
                <div className="space-y-2">
                  {sessionList.map((session) => (
                    <button
                      type="button"
                      key={session.id}
                      onClick={() => handleSelectSession(session.id)}
                      className="w-full text-left px-4 py-3 rounded-lg bg-[#1a1a1a] hover:bg-[#242424] transition-colors"
                    >
                      <div className="text-sm font-medium text-white line-clamp-1">{session.title || '未命名会话'}</div>
                      <div className="text-xs text-gray-400 mt-1 line-clamp-1">{session.last_message || '暂无消息'}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
